import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { IMAGE_SPECS, ImageSpec, ImageSpecKey } from "../utils/imageSpecs";

// Re-export so existing imports from this hook keep working
export { IMAGE_SPECS };
export type ImageType = ImageSpecKey;

export interface ImageDimensions {
    width: number;
    height: number;
}

export interface ImageValidationResult {
    isValid: boolean;
    warnings: string[];
    dimensions: ImageDimensions | null;
}

interface UseImageValidationOptions {
    type: ImageType;
    imageUrl?: string;
}

interface UseImageValidationReturn {
    warnings: string[];
    dimensions: ImageDimensions | null;
    isValidating: boolean;
    validateImage: (file: File) => Promise<ImageValidationResult>;
    validateImageUrl: (url: string) => Promise<ImageValidationResult>;
    clearValidation: () => void;
}

function getRatioTolerance(specs: ImageSpec): number {
    return specs.ratioTolerance ?? 0.1;
}

function getAcceptedRatios(specs: ImageSpec): number[] | null {
    if (specs.acceptedRatios && specs.acceptedRatios.length > 0) {
        return [...specs.acceptedRatios];
    }
    if (specs.ratio == null) {
        return null;
    }
    return [specs.ratio];
}

export function useImageValidation({ type, imageUrl }: UseImageValidationOptions): UseImageValidationReturn {
    const { t } = useTranslation();
    const [warnings, setWarnings] = useState<string[]>([]);
    const [dimensions, setDimensions] = useState<ImageDimensions | null>(null);
    const [isValidating, setIsValidating] = useState(false);

    const specs: ImageSpec = IMAGE_SPECS[type];

    const validateDimensions = useCallback(
        (width: number, height: number): string[] => {
            const validationWarnings: string[] = [];
            const actualRatio = width / height;
            const ratioTolerance = getRatioTolerance(specs);
            const acceptedRatios = getAcceptedRatios(specs);

            if (acceptedRatios && acceptedRatios.length > 0) {
                const matches = acceptedRatios.some((expected) => Math.abs(actualRatio - expected) <= ratioTolerance);
                if (!matches) {
                    if (type === "progression") {
                        validationWarnings.push((t as any)("imageWarningProgressionRatio"));
                    } else if (type === "avatar") {
                        validationWarnings.push((t as any)("imageWarningAvatarRatio"));
                    } else {
                        validationWarnings.push((t as any)("imageWarningRatio", { ratio: specs.ratioLabel ?? "" }));
                    }
                }
            }

            return validationWarnings;
        },
        [specs, type, t],
    );

    const validateImage = useCallback(
        async (file: File): Promise<ImageValidationResult> => {
            return new Promise((resolve) => {
                const img = new Image();
                img.onload = () => {
                    const dims = { width: img.width, height: img.height };
                    const validationWarnings = validateDimensions(img.width, img.height);

                    setDimensions(dims);
                    setWarnings(validationWarnings);

                    resolve({
                        isValid: validationWarnings.length === 0,
                        warnings: validationWarnings,
                        dimensions: dims,
                    });

                    URL.revokeObjectURL(img.src);
                };
                img.onerror = () => {
                    resolve({
                        isValid: false,
                        warnings: [(t as any)("imageLoadError") || "Failed to load image"],
                        dimensions: null,
                    });
                };
                img.src = URL.createObjectURL(file);
            });
        },
        [validateDimensions, t],
    );

    const validateImageUrl = useCallback(
        async (url: string): Promise<ImageValidationResult> => {
            if (!url) {
                return { isValid: true, warnings: [], dimensions: null };
            }

            setIsValidating(true);

            return new Promise((resolve) => {
                const img = new Image();
                img.crossOrigin = "anonymous";
                img.onload = () => {
                    const dims = { width: img.width, height: img.height };
                    const validationWarnings = validateDimensions(img.width, img.height);

                    setDimensions(dims);
                    setWarnings(validationWarnings);
                    setIsValidating(false);

                    resolve({
                        isValid: validationWarnings.length === 0,
                        warnings: validationWarnings,
                        dimensions: dims,
                    });
                };
                img.onerror = () => {
                    setIsValidating(false);
                    resolve({
                        isValid: true,
                        warnings: [],
                        dimensions: null,
                    });
                };
                img.src = url;
            });
        },
        [validateDimensions],
    );

    const clearValidation = useCallback(() => {
        setWarnings([]);
        setDimensions(null);
    }, []);

    useEffect(() => {
        if (imageUrl) {
            validateImageUrl(imageUrl);
        } else {
            clearValidation();
        }
    }, [imageUrl, validateImageUrl, clearValidation]);

    return {
        warnings,
        dimensions,
        isValidating,
        validateImage,
        validateImageUrl,
        clearValidation,
    };
}
